// クラシック版の完成画面（段階 C の2 の 6。2026-10-26 に chibi.js の makeComplete・setMakeComplete、charat.common.js の writeImageFromSVG、
// 画面ファイル legacy.js・gen1a.js・gen1b.js（NAME_ARC の if の中）の版から描き直した。動きと出来上がりの画像は前と同じ）。
// 完成のボタン → makeComplete（名前の文字を隠して setMakeComplete、終わったら戻す）→ setMakeComplete（完成画面を用意して writeImageFromSVG で画像にし、出す）。
// 名前とコピーライトは SVG を画像にしても書体が入らないので、できた canvas にあとから描く（描き方 3 通り）:
//   エンジンのまま: 名前を小さく縁取り付きで下に（コピーライトは描かない）。完成の前に目パチを止める
//   系統のある画面（前の legacy.js）: 名前とコピーライト。storm（RESULT_NAME_BIG）は名前 4 倍・白い縁取り・コピーライトも大きく
//   第1世代の円弧（NAME_ARC。前の gen1a.js・gen1b.js）: 名前は拡大して見る段に合わせた大きさ・位置、MODE_TEXT_ARC なら円弧に沿って（drawArcText。前は jCanvas）。blanc は TEXT_SERIF
// 完成画面を出す setMakeComplete は 2 通り: エンジンのまま（画像ができてからスライド）／第1世代 a の円弧（前の gen1a.js。画像づくりと同時にスライド、
// 閉じるといつも作る画面を読み直す、DL のボタンにも処理を付ける、「完成!」の既定の文言が " Done! /"）。
// 大きさ・CSS・動き・クリックは jq.ts（jQuery と同じ決まり、2026-10-26 の 7g-2）。円弧の名前は drawArcText（前は jCanvas）。
// お披露目に送る名前なしの版（charatOhirome）と、背景の覚え（lastComposite）も前と同じ
import { chibi, common } from './engine.js';
import { screen } from './colors.js';
import * as fx from './fx.js';
import * as jq from './jq.js';

type Engine = Record<string, any>;
const E = (): Engine => chibi() as unknown as Engine;
const C = (): Record<string, any> => common() as unknown as Record<string, any>;
const isJapanese = (): boolean => (window as unknown as { charat: { IsJapanese(): boolean } }).charat.IsJapanese();
const tt = (): Record<string, string> | undefined => (typeof _t !== 'undefined' && _t ? _t : undefined);
const nameEl = (): Element | null => document.querySelector('#' + E().SVG_TEXT + 'name');
const nameAttr = (a: string): any => nameEl()!.getAttribute(a);
/** 名前の文字（の入れ物）を隠す・戻す（d3 の attr("display") と同じ。無ければ何もしない） */
const setDisplay = (sel: string, v: string): void => {
  const el = document.querySelector(sel);
  if (el) el.setAttribute('display', v);
};
type WriteCanvas = (canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D, w: number, h: number) => void;

/**
 * 円弧に沿って文字を描く（前は jCanvas 21.0.1 の drawText の radius。Copyright Caleb Evans, MIT License。描き方は同じ）。
 * 1 文字ずつ、(x, y) を中心に letterSpacing（π の何倍か）ずつ回して、半径 radius の所に描く。flipArcText なら逆順にして上下を返す（円の下に読める向き）。
 * jCanvas と同じく、textBaseline・textAlign・font はこのあとも残る（続けて描くコピーライトもその設定で描かれる）。ほかの設定は元に戻す
 */
function drawArcText(ctx: CanvasRenderingContext2D, o: { fillStyle: string; fontFamily: string; fontSize: number; text: string; x: number; y: number; radius: number; rotate: number; letterSpacing: number; flipArcText: boolean }): void {
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'center';
  const fontSize = isNaN(Number(o.fontSize)) ? String(o.fontSize) : o.fontSize + 'px';
  ctx.font = 'normal ' + fontSize + ' ' + o.fontFamily;
  ctx.save();
  if (o.rotate) {
    ctx.translate(o.x, o.y);
    ctx.rotate(o.rotate * (Math.PI / 180));
    ctx.translate(-o.x, -o.y);
  }
  if (o.fillStyle) ctx.fillStyle = o.fillStyle;
  ctx.strokeStyle = 'transparent';
  ctx.lineWidth = 1;
  ctx.lineCap = 'butt';
  ctx.lineJoin = 'miter';
  ctx.miterLimit = 10;
  ctx.setLineDash([]);
  ctx.lineDashOffset = 0;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 0;
  ctx.shadowBlur = 0;
  ctx.shadowColor = 'transparent';
  ctx.globalAlpha = 1;
  ctx.globalCompositeOperation = 'source-over';
  ctx.imageSmoothingEnabled = true;
  const l = parseFloat(fontSize);
  let radius = o.radius, spacing = o.letterSpacing;
  for (const line of String(o.text).split('\n')) {
    ctx.save();
    ctx.translate(o.x, o.y);
    const j = o.flipArcText ? line.split('').reverse().join('') : line;
    const m = j.length;
    ctx.rotate((-Math.PI * spacing * (m - 1)) / 2);
    for (let i = 0; i < m; i += 1) {
      if (i !== 0) ctx.rotate(Math.PI * spacing);
      ctx.save();
      ctx.translate(0, -radius);
      if (o.flipArcText) ctx.scale(-1, -1);
      ctx.fillText(j[i], 0, 0);
      if (o.fillStyle !== 'transparent') ctx.shadowColor = 'transparent';
      ctx.strokeText(j[i], 0, 0);
      ctx.restore();
    }
    radius -= l;
    spacing += l / (1e3 * Math.PI);
    ctx.restore();
  }
  ctx.restore();
}

/** 系統（完成の描き方） */
const variant = (): 'new' | 'legacy' | 'arc' => {
  const scr = screen();
  if (scr === 'new') return 'new';
  if ((scr === 'gen1a' || scr === 'gen1b') && E().config('NAME_ARC', false)) return 'arc';
  return 'legacy';
};

// ---- 名前とコピーライトの描き方 ----

/** エンジンのまま: 名前を小さく、縁取り付きで下に。コピーライトは描かない（書体と色だけ合わせる。前のまま） */
const textNew: WriteCanvas = (aCanvas, ctx, aW, aH) => {
  const e = E();
  const drawScale = 1;
  const fontScale = (1.9 * aW) / e.SIZE_BASE_PIC;
  const nameFontSize = (e.MODE_TEXT ? 43 : 25) * fontScale * drawScale * 0.55;
  const nameY = e.MODE_TEXT ? nameFontSize * 3.526 * e.SCALE_MAKE * e.SCALE_SAVE : nameFontSize * 2.9;
  const crFontSize = 12 * fontScale * drawScale;
  const c = ctx as any;
  c.textAlign = 'center';
  c.fillStyle = nameAttr('fill');
  c.font = nameFontSize + 'px ' + e.FONT_NAME;
  // 注意: canvas は lineWidth = 0 を無視する（前の値＝既定 1 のまま）。下の strokeText は 1px の縁取りになる
  c.lineWidth = 0;
  c.strokeStyle = nameAttr('stroke');
  if (e.MODE_TEXT) {
    if (e.isViewText) c.fillText(nameAttr('textdata'), aW / 2, nameY / 1.18, aW);
  } else {
    c.strokeText(nameAttr('textdata'), aW / 2, aH - crFontSize * 2, aW);
    c.fillText(nameAttr('textdata'), aW / 2, aH - crFontSize * 2, aW);
  }
  c.font = crFontSize + 'px ' + e.FONT_CR;
  c.fillStyle = e.COLOR_CR;
};

/** 系統のある画面: 名前とコピーライト（storm は RESULT_NAME_BIG） */
const textLegacy: WriteCanvas = (canvas, ctx, w, h) => {
  const e = E();
  const big = e.config('RESULT_NAME_BIG', false);
  const dscale = 1;
  const ftscale = (1.9 * w) / e.SIZE_BASE_PIC;
  const fnsize = big ? (e.MODE_TEXT ? 43 : 25) * ftscale * dscale * 4 : (e.MODE_TEXT ? 43 : 25) * ftscale * dscale;
  const fnypos = e.MODE_TEXT ? fnsize * 3.526 * e.SCALE_MAKE * e.SCALE_SAVE : fnsize * 2.9;
  const fcsize = big ? 20 * ftscale * dscale : 12 * ftscale * dscale;
  const c = ctx as any;
  c.textAlign = 'center';
  c.fillStyle = nameAttr('fill');
  c.font = fnsize + 'px ' + e.FONT_NAME;
  if (big) {
    c.lineWidth = 20;
    c.strokeStyle = nameAttr('stroke');
  }
  if (e.MODE_TEXT) {
    if (e.isViewText) c.fillText(nameAttr('textdata'), w / 2, fnypos, w);
  } else if (big) {
    c.strokeText(nameAttr('textdata'), w / 2, h - fcsize * 2, w);
    c.fillText(nameAttr('textdata'), w / 2, h - fcsize * 2, w);
  } else {
    c.fillText(nameAttr('textdata'), w / 2, h - fcsize * 1.3, w);
  }
  c.font = fcsize + 'px ' + e.FONT_CR;
  c.fillStyle = e.COLOR_CR;
  c.fillText(copyrightText(), w / 2, h - fcsize * 0.3, w);
};

/** 第1世代の円弧: 名前は拡大して見る段に合わせる。MODE_TEXT_ARC なら円弧に沿って（drawArcText。前は jCanvas）（blanc の台詞は TEXT_SERIF） */
const textArc: WriteCanvas = (_canvas, ctx, u, r) => {
  const e = E();
  const serif = e.config('TEXT_SERIF', false);
  const dscale = 1;
  const o = (1.9 * u) / e.SIZE_BASE_PIC;
  let t = (e.MODE_TEXT ? (serif ? 15 : 43) : 12.3) * o * dscale;
  let v = e.MODE_TEXT ? t * (serif ? 4.05 : 3.526) * e.SCALE_MAKE * e.SCALE_SAVE : t * 15.7;
  const s = 12 * o * dscale;
  const z = e.btnSubZoom;
  if (z.EXPOS != 0) {
    t = t * z.POS[z.EXPOS].SC;
    v = v * (z.POS[z.EXPOS].SC * (e.isMobile ? 0.814 : 0.772));
  }
  const c = ctx as any;
  c.textAlign = 'center';
  c.fillStyle = nameAttr('fill');
  c.font = t + 'px ' + e.FONT_NAME;
  const q = nameAttr('fill');
  if (e.MODE_TEXT) {
    if (e.isViewText) c.fillText(nameAttr('textdata'), u / 2, v, u);
  } else if (e.MODE_TEXT_ARC) {
    drawArcText(ctx, {
      fillStyle: q,
      fontFamily: e.FONT_NAME,
      fontSize: t,
      text: nameAttr('textdata'),
      x: u / 2,
      y: r / 2,
      radius: u * z.POS[z.EXPOS].SC * (e.isMobile ? 0.242 : 0.227),
      rotate: 180,
      letterSpacing: 0.06,
      flipArcText: true,
    });
  } else {
    c.fillText(nameAttr('textdata'), u / 2, v, u);
  }
  c.font = s + 'px ' + e.FONT_CR;
  c.fillStyle = e.COLOR_CR;
  c.fillText(copyrightText(), u / 2, r - s * 0.6, u);
};

/** コピーライトの文字（d3 の text() と同じ。要素が無ければ前と同じく例外） */
const copyrightText = (): string => document.querySelector('#' + E().SVG_TEXT + 'copyright')!.textContent!;

// ---- 完成 ----

/** 完成のボタンのあと: 名前の文字を隠して完成画面を出し、戻す */
export function makeComplete(): void {
  const e = E(), v = variant();
  setDisplay('#' + e.SVG_TEXT + 'gname', 'none');
  setDisplay('#' + e.SVG_TEXT, 'none');
  // エンジンのまま: 目パチを止める（stopEyeBlinkAnimation はパーツを替えたときに初めて作られる）
  if (v === 'new' && typeof C().stopEyeBlinkAnimation === 'function') C().stopEyeBlinkAnimation();
  const save = document.querySelector('#' + e.SVG_SAVE);
  e.setMakeComplete(
    true,
    e.DIV_F_CONTENTS,
    '#' + e.SVG_SAVE,
    '#' + e.SVG_BGSAVE,
    save ? save.getAttribute('width') : null,
    save ? save.getAttribute('height') : null,
    e.SCALE_SAVE,
    v === 'new' ? textNew : v === 'arc' ? textArc : textLegacy,
  );
  setDisplay('#' + e.SVG_TEXT, 'block');
  setDisplay('#' + e.SVG_TEXT + 'gname', 'block');
}

/**
 * 完成画面を出す（aComplete が偽なら閉じる）。前景 source・背景 sbg の SVG を画像にして出し、できた canvas に fnWriteCanvas で文字を描く。
 * 注意: sw・sh・aScale は使っていない（writeImageFromSVG には drawScale=1。前のまま）
 */
export function setMakeComplete(aComplete: boolean, aWindow: string, source: string, sbg: string, _sw: unknown, _sh: unknown, _aScale: unknown, fnWriteCanvas?: WriteCanvas): void {
  const e = E();
  if (!aComplete) {
    jq.fadeOut(e.DIV_F_MAKE_RSTBG, 400);
    return;
  }
  // 第1世代 a の円弧（前の gen1a.js）は makeComplete が描き方を渡さない形だったので、ここで円弧の描き方
  const gen1aArc = screen() === 'gen1a' && variant() === 'arc';
  const write = gen1aArc ? textArc : fnWriteCanvas!;
  e.setMakeCompleteLoadOk = false;
  const windowH = jq.height(aWindow);
  const windowW = jq.width(aWindow);
  const drawScale = 1;
  // 完成画面を出す用意（PC は下に置いてから上へスライド、スマホは全体の高さを合わせる）
  if (!e.isMobile) {
    jq.setCss(e.DIV_F_MAKE_RSTBG, 'display', 'block');
    jq.setCss(e.DIV_F_MAKE_RSTBG, 'opacity', 1);
    jq.setCss(e.DIV_F_MAKE_RESULT, 'top', jq.outerHeight(e.DIV_F_MAKE_RESULT));
    jq.setCss(e.DIV_F_MAKE_RESULT, 'left', (windowW - jq.outerWidth(e.DIV_F_MAKE_RESULT)) / 2);
    jq.setCss(e.DIV_F_MAKE_RESULT, 'opacity', 1);
  } else {
    jq.setHeight(e.DIV_F_CONTENTS, jq.height(e.DIV_F_ROOT));
  }
  // 「完成!」の文言（出すのは画像ができてから）
  jq.setCss(e.DIV_F_MAKE_RSTMES, 'opacity', 0);
  const t = tt();
  let resultMes: string;
  if (gen1aArc) {
    // 前の gen1a.js の既定の文言（"\ " は空白になる）
    resultMes = t && t.complete ? t.complete : ' Done! /';
  } else if (e.config('RESULT_MES_JA_ONLY', false)) {
    // portrait: 翻訳の文言は日本語のときだけ。ほかの言語は "\Complete!/"
    resultMes = isJapanese() ? (t && t.complete ? t.complete : ' Done! /') : '\\Complete!/';
  } else {
    resultMes = t && t.complete ? t.complete : '\\ Done! /';
  }
  jq.setText(e.DIV_F_MAKE_RSTMES, resultMes);
  // 描く所の大きさ（完成画面の短い辺の 8 割）
  let viewSize = Math.min(jq.innerWidth(e.DIV_F_MAKE_RESULT), jq.innerHeight(e.DIV_F_MAKE_RESULT));
  viewSize *= 0.8;
  jq.setCss(e.DIV_F_CIMG, 'top', 0);
  jq.setCss(e.DIV_F_CIMG, 'left', (jq.innerWidth(e.DIV_F_MAKE_RESULT) - viewSize) / 2);
  jq.setCss(e.DIV_F_CIMG, 'height', viewSize);
  jq.setCss(e.DIV_F_CIMG, 'width', viewSize);
  jq.setCss(e.DIV_F_CIMG_VIEW, 'height', viewSize);
  jq.setCss(e.DIV_F_CIMG_VIEW, 'width', viewSize);
  jq.setCss(e.DIV_F_CIMG_SHARE, 'height', viewSize);
  jq.setCss(e.DIV_F_CIMG_SHARE, 'width', viewSize);
  // スマホは高さを中身に合わせる
  if (e.isMobile) {
    jq.setHeight(
      e.DIV_F_MAKE_RESULT,
      Math.ceil(
        (jq.height(e.DIV_F_ADS_MB) +
          jq.height(e.DIV_F_ADS_RESULT) +
          jq.height(e.DIV_F_MAKE_RSTMES) +
          jq.height(e.DIV_F_CIMG) +
          jq.height(e.DIV_F_MAKE_RSTINF) +
          jq.height(e.DIV_F_MAKE_SHAREB) * 2 +
          jq.height('#chaMakeResultOtherApp') +
          jq.height('#chaMakeResultReturnBtn')) *
          1.3,
      ),
    );
  }
  // スライドして出す（終わったら「完成!」を出して、閉じられるように）
  const slideIn = (): void => {
    const done = (): void => {
      jq.setCss(E().DIV_F_MAKE_RSTMES, 'opacity', 1);
      E().setMakeCompleteLoadOk = true;
    };
    if (E().isMobile) jq.animate(E().DIV_F_MAKE_RSTBG, { top: '0px' }, 1000, 'easeOutQuint', done);
    else jq.animate(E().DIV_F_MAKE_RESULT, { top: (windowH - jq.outerHeight(E().DIV_F_MAKE_RESULT)) / 2 + 'px' }, 1000, 'easeOutQuint', done);
  };
  if (gen1aArc) {
    // 前の gen1a.js: 画像づくりを始めたらすぐスライドし、そのあとで初めのクリックを付ける
    writeImageFromSVG(e.DIV_F_CIMG_VIEW, e.DIV_F_CIMG_SHARE, e.SHARE_IMGRATE, source, sbg, e.SIZE_SAVE_PIC, e.SIZE_SAVE_PIC, drawScale, write);
    slideIn();
    initClicks(true);
  } else {
    initClicks(false);
    // 画像にして出し、できたら文字を描いてスライド
    writeImageFromSVG(e.DIV_F_CIMG_VIEW, e.DIV_F_CIMG_SHARE, e.SHARE_IMGRATE, source, sbg, e.SIZE_SAVE_PIC, e.SIZE_SAVE_PIC, drawScale, (aCanvas: any, aContext: CanvasRenderingContext2D, aW: number, aH: number) => {
      write(aCanvas, aContext, aW, aH);
      slideIn();
    });
  }
}

/**
 * 完成画面のクリック（はじめの 1 回だけ）: 中は何もしない、閉じる・戻る、サイトのトップへ。
 * gen1aArc（前の gen1a.js）は閉じるといつも作る画面を読み直し（ホームのボタンはメーカーのトップへ）、DL のボタンにも処理を付ける
 */
function initClicks(gen1aArc: boolean): void {
  const e = E();
  if (e.setMakeCompleteInit) return;
  jq.on(e.DIV_F_MAKE_RESULT, 'click', (ev: Event) => {
    ev.stopPropagation();
  });
  jq.on('#chaMakeResultBg, #chaMakeResultBtnHome, #chaMakeReturn', 'click', function (this: Element) {
    const en = E();
    if (!en.setMakeCompleteLoadOk) return;
    // ホームのボタンならメーカーのトップへ。それ以外は既定でトップへ、RESULT_CLOSE_TO_TOP: false なら作る画面（今のページ）を読み直す
    const isRoot = jq.attr(this, 'id') == jq.attr('#chaMakeResultBtnHome', 'id');
    const reTime = new Date().getTime();
    const slideOut = (): void => {
      jq.animate(E().DIV_F_MAKE_RESULT, { top: -jq.outerHeight(E().DIV_F_MAKE_RESULT) + 'px', opacity: 0 }, 300, 'easeOutQuint');
    };
    if (en.MODE_RELOAD) {
      // 白くフェードしてからページを移る（読み直しの印をセッションに残す）
      // 前は background-color も '#FFFFFF' へ動かすと書いていたが、2026-10-20 に jQuery UI（色を動かす部品）を外してからは何も起きていなかった（opacity だけ）
      jq.delay(en.DIV_F_MAKE_RSTBG, 50);
      jq.animate(en.DIV_F_MAKE_RSTBG, { opacity: 1 }, 350, 'linear', () => {
        C().setStrage(E().CACHE_RELOAD, reTime, false);
        // 作る画面を読み直すときは今のページの URL（2026-10-26。前は "." で、クラシック版 …/create/classic からだと …/create/＝新しい作る画面に移っていた）
        window.location.href = (gen1aArc ? isRoot : E().config('RESULT_CLOSE_TO_TOP', true) || isRoot) ? '../' : location.pathname;
      });
      slideOut();
    } else {
      // その場で完成画面を閉じる
      jq.delay(en.DIV_F_MAKE_RSTBG, 50);
      jq.animate(en.DIV_F_MAKE_RSTBG, { opacity: 0 }, 350, 'linear', () => {
        jq.setCss(E().DIV_F_MAKE_RSTBG, 'display', 'none');
      });
      slideOut();
    }
  });
  // サイトのトップへ
  jq.on('#chaMakeReturnHome', 'click', () => {
    if (E().setMakeCompleteLoadOk) window.location.href = '../../';
  });
  if (gen1aArc) {
    // DL のボタン（前の gen1a.js。完成画像をそのままダウンロード）
    jq.on('#chaMakeResultBtnDL', 'click', () => {
      const a = document.createElement('a');
      a.setAttribute('id', 'downloadLink');
      a.setAttribute('class', 'downloadLink');
      a.setAttribute('download', C().getSaveFileName(E().SERVICE_NAME, 'png'));
      const src = jq.attr(E().DIV_F_CIMG_VIEW, 'src');
      if (src != null) a.setAttribute('href', src);
      a.textContent = 'CHARAT';
      a.style.setProperty('display', 'none');
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        a.remove();
      }, 10);
    });
  }
  e.setMakeCompleteInit = true;
}

// ---- SVG を画像に ----

const XMLNS = 'http://www.w3.org/2000/xmlns/';
/** SVG の要素に名前空間と版を付けて、data URI にする（d3 の attr("xmlns")・attr("version") と同じ） */
function svgDataUri(svg: Element): string {
  svg.setAttributeNS(XMLNS, 'xmlns', 'http://www.w3.org/2000/svg');
  svg.setAttribute('version', '1.1');
  return 'data:image/svg+xml;utf-8,' + encodeURIComponent(new XMLSerializer().serializeToString(svg));
}

/**
 * 前景 svgfsrc・背景 svgbsrc の SVG を画像にして imgarea（img のセレクタ）に入れ、sharearea（同じ）には JPEG（sharerough）を入れる。
 * sw・sh: 画像の大きさ、aScale: 前景の倍率、fnWriteCanvas: できた canvas に文字を描く関数。
 * お披露目に送る画像は名前を入れない（2026-10-22）: 名前を空にして同じ描き方で描いた版を、完成画像の要素の charatOhirome に src と組で持たせる。
 * 吹き出しに名前を出すメーカー（MODE_TEXT で吹き出しを出しているとき。portrait simple4）は、吹き出しの層も外した前景で描く
 */
export function writeImageFromSVG(imgarea: string, sharearea: string | null, sharerough: number, svgfsrc: string, svgbsrc: string | null, sw: number, sh: number, aScale: number, fnWriteCanvas?: WriteCanvas): void {
  const c = C();
  // 背景の SVG と倍率を覚えておく（完成画面の部品 additional.js が、画像DL・高画質DL に背景を同じ置き方で描くのに使う。2026-10-24〜）
  c.lastComposite = { bg: svgbsrc, scale: Number(aScale) || 1 };
  const whScale = 1 + (1 - aScale);
  sw *= whScale;
  sh *= whScale;
  const fsvg = document.querySelector(svgfsrc)!;
  const fdataUri = svgDataUri(fsvg);
  // 描く canvas（ページの最後に隠して置く）
  const canvas = document.createElement('canvas');
  canvas.setAttribute('id', 'IDwriteImageFromSVG');
  canvas.setAttribute('width', String(sw));
  canvas.setAttribute('height', String(sh));
  canvas.style.setProperty('display', 'none');
  document.body.appendChild(canvas);
  const context = canvas.getContext('2d')!;
  const bimg = new Image(sw, sh);
  const fimg = new Image(sw, sh);
  context.clearRect(0, 0, sw, sh);
  // お披露目の版の前景（吹き出しの層を外したもの）
  const e = E();
  let odataUri: string | null = null;
  if (e && e.MODE_TEXT && e.isViewText) {
    const textLayer = "[id='" + e.ATTR_LAYER + e.PARTS_TEXT + "']";
    if (fsvg.querySelector(textLayer)) {
      const ocopy = fsvg.cloneNode(true) as Element;
      const olayer = ocopy.querySelector(textLayer)!;
      olayer.parentNode!.removeChild(olayer);
      odataUri = 'data:image/svg+xml;utf-8,' + encodeURIComponent(new XMLSerializer().serializeToString(ocopy));
    }
  }
  let oimg: HTMLImageElement | null = odataUri ? new Image(sw, sh) : null;
  let waiting = oimg ? 2 : 1; // 読み込みを待つ前景の数
  const bgEl = svgbsrc != null ? document.querySelector(svgbsrc) : null;
  // 背景を描いたら前景を読む（背景が縦長なら高さ、横長なら幅を合わせて真ん中に。属性の値は文字のまま比べる＝前のまま）
  bimg.onload = () => {
    const bh = bgEl!.getAttribute('height') as string, bw = bgEl!.getAttribute('width') as string;
    if (bh > bw) {
      const shadj = (bh as unknown as number) / (bw as unknown as number);
      context.drawImage(bimg, 0, 0, sw, sh * shadj);
    } else {
      const swadj = (bw as unknown as number) / (bh as unknown as number);
      const swpos = -1 * ((sw * swadj - sw) / 2);
      context.drawImage(bimg, swpos, 0, sw * swadj, sh);
    }
    loadForeground();
  };
  bimg.onerror = () => {};
  function loadForeground(): void {
    fimg.src = fdataUri;
    if (oimg) oimg.src = odataUri!;
  }
  fimg.onload = () => {
    if (--waiting === 0) drawForeground();
  };
  if (oimg) {
    oimg.onload = () => {
      if (--waiting === 0) drawForeground();
    };
    // 吹き出しを外した前景が読めなければ、名前を消すだけにする（完成画面は止めない）
    oimg.onerror = () => {
      oimg = null;
      if (--waiting === 0) drawForeground();
    };
  }
  function drawForeground(): void {
    const cw = canvas.width, ch = canvas.height;
    const base = oimg ? context.getImageData(0, 0, cw, ch) : null; // 背景だけの画素
    context.drawImage(fimg, (sw * aScale - sw) / 2, (sh * aScale - sh) / 2, sw, sh);
    // 文字（SVG では書体を中に入れないと出ないので canvas に描く）。お披露目の版を先に作り、画素を戻してから名前ありで描く
    let ohirome: string | null = null;
    const nameNode = fnWriteCanvas != undefined && E() ? document.getElementById(E().SVG_TEXT + 'name') : null;
    const nameText = nameNode ? nameNode.getAttribute('textdata') : null;
    if (oimg || nameText) {
      const plain = context.getImageData(0, 0, cw, ch);
      if (oimg) {
        context.putImageData(base!, 0, 0);
        context.drawImage(oimg, (sw * aScale - sw) / 2, (sh * aScale - sh) / 2, sw, sh);
      }
      if (fnWriteCanvas != undefined) {
        const noText = oimg ? context.getImageData(0, 0, cw, ch) : plain;
        if (nameText) nameNode!.setAttribute('textdata', '');
        try {
          fnWriteCanvas(canvas, context, sw, sh);
        } catch (err) {
          context.putImageData(noText, 0, 0); // 描けなかったら文字なし
        } finally {
          if (nameText) nameNode!.setAttribute('textdata', nameText);
        }
      }
      ohirome = canvas.toDataURL('image/png');
      context.putImageData(plain, 0, 0);
    }
    if (fnWriteCanvas != undefined) fnWriteCanvas(canvas, context, sw, sh);
    const png = canvas.toDataURL('image/png');
    jq.setAttr(imgarea, 'src', png);
    // お披露目（additional.js の charatImgPublic）が送る画像。src と組で持ち、src が別の画像に変わっていたら使わない
    for (const el of jq.els(imgarea)) (el as Element & { charatOhirome?: unknown }).charatOhirome = { src: png, image: ohirome || png };
    if (sharearea != null) jq.setAttr(sharearea, 'src', canvas.toDataURL('image/jpeg', sharerough));
    // 片付け
    bimg.remove();
    fimg.remove();
    if (oimg) oimg.remove();
    canvas.remove();
  }
  fimg.onerror = () => {};
  // 描き始め（背景があれば背景から。背景のセレクタがあって要素が無いときは前と同じく例外）
  if (svgbsrc != null) bimg.src = svgDataUri(bgEl!);
  else loadForeground();
}
